import * as React from 'react';
import { Group, ValidationResult } from 'rcd-cpp-ui/ui/form/Group';
import { DataType } from '../../../enums/DataType';
import { FieldProps } from '../metadataFormField';
import { TreeFieldProps } from '../TreeField/treeField';
import { ValidationResponse } from '../../../types/ValidationResponse';
import { removeDuplicates } from 'rcd-cpp-workspace/utils/array';
import {
  fieldIsRequired,
  fieldIsRequiredNext,
  getReferenceValue,
  isRequiredFieldValueEmpty,
  skipConfigureWorkflowValidation
} from '../../../utils';
import { WYSIWYGEditor } from '../../WYSIWYGEditor/WYSIWYGEditor';
import { SchemaField } from '../../../types';
import TooltipPopover from '../../Popover/popover';
import { useSelector } from '../../../utils/useSelector';
import './fieldGroup.scss';
import { WorkflowPrioritization } from '../../../enums/WorkflowPrioritization';
import { ValidationMode } from '../../../enums/ValidationMode';
import { getFieldLabel } from '../../../utils';

export const ModalPushResponseContext =
  React.createContext<ValidationResponse | null>(null);

interface Props extends Pick<Partial<TreeFieldProps>, 'settings'>, FieldProps {
  value: any;
  children: any;
  componentRight?: React.ReactNode | (() => React.ReactNode);
}

const getValidationResponse = (
  r: ValidationResponse,
  { field, settings, schema }: Partial<Props>
): ValidationResponse => {
  //if (schema?.props?.type != NodeType.Item) return null;
  if (!r) return null;
  const keys = [field.key];
  settings?.forEach(s => {
    if (s?.field?.key) keys.push(s.field.key);
  });
  return {
    errors: r.errors?.filter(e => keys.includes(e.key)),
    warnings: r.warnings?.filter(w => keys.includes(w.key))
  };
};

export const FieldGroup = (props: Props) => {
  const { field, disabled, children, value, custom, data } = props;
  const modalPushResponse = React.useContext(ModalPushResponseContext);
  const reduxPushResponse = useSelector(s => s.form?.pushResponse);
  const pushResponse =
    modalPushResponse != null ? modalPushResponse : reduxPushResponse;
  const fieldErrors = useSelector(s => s.form?.fieldErrors);
  let shouldValidate = props?.shouldValidate;
  if (
    skipConfigureWorkflowValidation(data) &&
    field.key === 'Item.configureWorkflowType'
  ) {
    shouldValidate = ValidationMode.None;
  }
  const validateResponse = merge(
    getValidationErrors({ ...props, shouldValidate }),
    merge(
      getValidationResponse(pushResponse, props),
      getValidationResponse(fieldErrors, props)
    )
  );
  const validation: ValidationResult = {};
  if (validateResponse?.errors?.length) {
    validation.error = removeDuplicates(
      validateResponse.errors.map(e => e.validationMessage).filter(x => x)
    );
  }
  if (validateResponse?.warnings?.length) {
    validation.warning = removeDuplicates(
      validateResponse.warnings.map(e => e.validationMessage).filter(x => x)
    );
  }

  const isMyItemsView = useSelector(s => s.view?.selected?.isMyItemsView);

  const { name, description, newItemName } = field;
  const isRequired = fieldIsRequired(field, data);
  const isRequiredNext = fieldIsRequiredNext(
    field,
    data,
    props.impliedDataProps
  );

  const label = React.useMemo(() => {
    if (props?.isEditMode) {
      return null;
    }
    const displayName = (
      <span>
        {getFieldLabel(data, newItemName, name)}
        {!isRequired && isRequiredNext && (
          <span className="required-next-asterisks">**</span>
        )}
      </span>
    );
    if (description) {
      return (
        <span className="field-with-description">
          {displayName}
          <TooltipPopover
            type="info"
            pointerPosition="right"
            className="field-description-tooltip"
            content={
              <div className="tooltip-content-wrapper">
                <WYSIWYGEditor
                  content={description}
                  readonly={true}
                  disabled={true}
                  onChange={null}
                />
              </div>
            }
          />
        </span>
      );
    } else {
      return displayName;
    }
  }, [isRequired, isRequiredNext, name, description]);

  const child = custom || (!disabled ? children : value) || '--';

  const shouldMask =
    !!isMyItemsView &&
    (field?.key == `Item.itemId` || field?.key == `Item.title`);
  const className = `${!!shouldMask ? 'fs-mask' : ''}`;
  return (
    <Group
      label={label as any}
      isRequired={isRequired}
      validate={validation}
      className={className}
      componentRight={props.componentRight}
    >
      {typeof child == 'function' ? child(validateResponse) : child}
    </Group>
  );
};

export const getValidationErrors = ({
  field,
  data,
  value,
  settings,
  shouldValidate,
  impliedDataProps
}: Partial<Props>): ValidationResponse => {
  if (!shouldValidate || shouldValidate == ValidationMode.None) return null;

  const isRequired =
    shouldValidate == ValidationMode.Next
      ? (field: SchemaField, data: any) => {
          return (
            fieldIsRequiredNext(field, data, impliedDataProps) ||
            fieldIsRequired(field, data)
          );
        }
      : fieldIsRequired;
  const getValue = (field: SchemaField, v: any) => {
    if (!impliedDataProps) return v;
    const f = field?.field;
    if (impliedDataProps[f]) return impliedDataProps[f];
    return v;
  };
  if (settings) {
    return {
      errors: settings
        .filter(f => isRequired(f.field, data))
        .map(f => {
          const fieldData = getValue(f?.field, data?.props[f?.field?.field]);
          if (isRequiredFieldValueEmpty(fieldData)) {
            return {
              key: f.field.key,
              validationMessage: `${getFieldLabel(data, f?.field?.newItemName, f?.field?.name)} is required.`
            };
          } else {
            return null;
          }
        })
        .filter(x => x)
    };
  } else {
    if (
      isRequired(field, data) &&
      field.dataType != DataType.boolean &&
      isRequiredFieldValueEmpty(getValue(field, value))
    ) {
      if (field.key == 'Item.configureWorkflowType') {
        if (
          getReferenceValue(data, 'workflowPrioritization', 'id') ==
          WorkflowPrioritization.FirstTake
        )
          return null;
      }
      return {
        errors: [
          {
            key: field.key,
            validationMessage: `${getFieldLabel(data, field?.newItemName, field?.name)} is required.`
          }
        ]
      };
    }
  }
};

export const merge = (
  v1: ValidationResponse,
  v2: ValidationResponse
): ValidationResponse => {
  if (!v1 && !v2) return v1;
  if (!v2) return v1;
  if (!v1) return v2;
  return {
    ...v1,
    ...v2,
    errors: []
      .concat(v1.errors)
      .concat(v2.errors)
      .filter(x => x),
    warnings: []
      .concat(v1.warnings)
      .concat(v2.warnings)
      .filter(x => x)
  };
};
